import { yupResolver } from "@hookform/resolvers/yup";
import { CheckCircledIcon, UploadIcon } from "@radix-ui/react-icons";
import { Button, Card, Flex, Heading, Spinner, Text } from "@radix-ui/themes";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useQueryClient } from "react-query";
import { DragAndDropFileUpload } from "../../components/inputs/FileUpload/DragAndDropFileUpload";
import { postFormData } from "../../utils/axios-config";
import { Key_universities, UPLOAD_IMAGES } from "../../utils/constants";
import { UploadImagesFormValidation } from "./UploadImagesFormValidation";

export type FileType = "favicon" | "logo" | "background";

interface UploadItemType {
  type: FileType;
  file: File;
  preview?: string;
  uploaded?: boolean;
}

interface UploadItemProps {
  type: FileType;
  initialPreview?: string;
}

export const UploadItem = ({
  type = "logo",
  initialPreview,
}: UploadItemProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<UploadItemType>({
    resolver: yupResolver(UploadImagesFormValidation),
    defaultValues: { type },
    mode: "onChange",
  });

  const handleFileSelect = (file?: File) => {
    if (!file) return;
    // Sem isto o `uploaded` nunca voltava a `false`: depois de um envio o botão
    // ficava preso em "Enviado" e desactivado, e só um refresh da página
    // permitia carregar uma imagem corrigida.
    setValue("uploaded", false);
    const reader = new FileReader();
    reader.onloadend = () => setValue("preview", reader.result as string);
    reader.readAsDataURL(file);
  };

  const preview = watch("preview");

  useEffect(() => {
    if (!initialPreview) return;
    setValue("preview", initialPreview);
  }, [initialPreview, setValue]);
  const queryClient = useQueryClient();
  const onSubmit = handleSubmit(async (data) => {
    try {
      const formData = new FormData();
      formData.append("file", data.file);
      formData.append("fileType", data.type);
      await postFormData(UPLOAD_IMAGES, formData);
      setValue("uploaded", true);
      toast.success("Imagem carregada com sucesso");
      queryClient.invalidateQueries(Key_universities);
    } catch (err: any) {
      // O `errorHandler` já normaliza o erro numa string pronta para toast;
      // um `catch` sem argumento deitava fora a mensagem do servidor.
      toast.error(err?.message || "Erro ao carregar imagem");
    }
  });

  const typeLabels: Record<FileType, string> = {
    favicon: "Favicon",
    logo: "Logótipo",
    background: "Fundo",
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      <Flex direction="column" gap="4">
        <Card size="3" mb="3">
          <Flex direction="row" gap="4" align="start">
            <Flex
              align="center"
              justify="center"
              style={{
                width: "150px",
                height: "150px",
                border: "2px dashed var(--gray-6)",
                borderRadius: "var(--radius-3)",
                overflow: "hidden",
                backgroundColor: "var(--gray-2)",
                color: "var(--gray-9)",
              }}
            >
              {preview ? (
                <img
                  src={preview}
                  alt={`Pré-visualização — ${typeLabels[type]}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <UploadIcon width="40" height="40" />
              )}
            </Flex>

            <Flex direction="column" gap="2" style={{ flex: 1 }}>
              <Heading size="4">
                {typeLabels[type]}
              </Heading>

              <Flex gap="2" mt="2">
                <Controller
                  name="file"
                  control={control}
                  render={({ field }) => (
                    <DragAndDropFileUpload
                      onFileDrop={(file) => {
                        field.onChange(file);
                        handleFileSelect(file!);
                      }}
                      label="Arraste ou selecione o ficheiro"
                      selectedFile={field.value}
                    />
                  )}
                />
                {errors.file && (
                  <Text color="red" size="1">{`${errors.file?.message}`}</Text>
                )}
                <Button
                  type="submit"
                  disabled={watch("uploaded") || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner loading>
                        <UploadIcon />
                      </Spinner>
                      A enviar…
                    </>
                  ) : watch("uploaded") ? (
                    <>
                      <CheckCircledIcon /> Enviado
                    </>
                  ) : (
                    <>
                      <UploadIcon /> Carregar ficheiro
                    </>
                  )}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      </Flex>
    </form>
  );
};
